import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Button from '../../BaseComponents/Button';
import './Appointments.scss';
import AppointmentsList from './AppointmentsListTable';
import TableHeader from '../../BaseComponents/TableHeader';
import AppointmentModal from './AppointmentModal/AppointmentModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  changeDeleteModalVisibility,
  changeModalVisibility,
  changeViewAppointmentResultsModalVisibility,
  setAppointmentsList
} from '../../../store/memberSlice';
import DeleteAppointmentModal from './DeleteAppointmentModal';
import { getAppointmentsList } from '../../../api/appointments';
import {  getTuliBookingInfo, getInuviBookingInfo } from '../../../api/pharma';
import {
  AppointmentLoadStrategy,
  AppointmentFilter,
  BiomarkerResultType,
  GetTuliBookingInfoResponse,
  AppScenariosAppointmentBasePayloadAppointmentInResponse,
  PharmaInfo,
  UserRoleEnum
} from '../../../types/apiGeneratedTypes';
import ShowBiomarkersTestResultsModal from '../../Alerts/ShowBiomarkersTestResultsModal';
import { toast } from 'react-toastify';
import InuviBookingModal from './InuviBookingModal';
import FutureTestsModal from '../../Appointments/FutureTestsModal';
import CreatePmpAlertModal from '../../Appointments/CreatePmpAlertModal';
import { FutureTestItem, getFutureTests, deleteFutureTest, getFutureTestById } from '../../../api/future-test';
import { deletePmpAlert, getPmpAlerts, PmpAlertItem, PmpAlertStatus } from '../../../api/pmp-alert';
import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import PmpAlertInlineStatusSelect from '../../Appointments/PmpAlertInlineStatusSelect';
import ConfirmModal from '../../BaseComponents/Modal/ConfirmModal';
import {
  getFutureTestModalityLabel,
  normalizeFutureTestModalityKey,
  MRI_BODY_PART_OPTIONS,
} from '../../../utils/constants';

// Extend AppointmentInResponse to include pharmacy
interface ExtendedAppointmentInResponse
  extends AppScenariosAppointmentBasePayloadAppointmentInResponse {
  pharmacy?: PharmaInfo;
}

export type AppointmentsTableActiveTab =
  | 'upcoming'
  | 'past'
  | 'all'
  | 'future-tests'
  | 'pmp-alerts';

const BLOODS_MODALITY = 'BLOODS';
const MRI_MODALITY = 'MRI';

const resolveFutureTestModality = (futureTest: FutureTestItem): string | null => {
  if (futureTest.modality) {
    return normalizeFutureTestModalityKey(String(futureTest.modality));
  }

  const anyTest = futureTest as FutureTestItem & Record<string, unknown>;
  const altModality = anyTest.test_modality ?? anyTest.modality_type;
  if (typeof altModality === 'string' && altModality) {
    return normalizeFutureTestModalityKey(altModality);
  }

  if (
    futureTest.panel_ids?.length ||
    futureTest.panel ||
    futureTest.panels?.length
  ) {
    return BLOODS_MODALITY;
  }
  if (futureTest.body_parts?.length) return MRI_MODALITY;
  return null;
};

const resolveScheduledMonthYear = (
  futureTest: FutureTestItem
): string | null => {
  if (futureTest.scheduled_month_year) return futureTest.scheduled_month_year;

  const anyTest = futureTest as FutureTestItem & Record<string, unknown>;
  const alt =
    anyTest.scheduled_month ||
    anyTest.month_year ||
    anyTest.shaduled_month_year;
  if (typeof alt === 'string' && alt.trim()) return alt.trim();
  return null;
};

const getFutureTestDetailsLabel = (futureTest: FutureTestItem): string => {
  const modality = resolveFutureTestModality(futureTest);

  if (
    modality === BLOODS_MODALITY ||
    futureTest.panel_ids?.length ||
    futureTest.panel ||
    futureTest.panels?.length
  ) {
    if (futureTest.panels?.length) {
      return futureTest.panels.map((panel) => panel.name).filter(Boolean).join(', ');
    }
    if (futureTest.panel?.name) {
      return futureTest.panel.name;
    }
    if (futureTest.panel_ids?.length) {
      return futureTest.panel_ids.join(', ');
    }
  }

  if (
    (modality === MRI_MODALITY || futureTest.body_parts?.length) &&
    futureTest.body_parts?.length
  ) {
    return futureTest.body_parts
      .map((bodyPart) => {
        const match = MRI_BODY_PART_OPTIONS.find(
          (option) => option.key === bodyPart || option.value === bodyPart
        );
        return match?.value ?? bodyPart;
      })
      .join(', ');
  }

  return '-';
};

/** Parses YYYY-MM or MM/YYYY into a Date, otherwise null. */
const parseMonthYearValue = (value: string): Date | null => {
  const trimmed = value.trim();

  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})$/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]);
    if (year && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const month = Number(slashMatch[1]);
    const year = Number(slashMatch[2]);
    if (year && month >= 1 && month <= 12) {
      return new Date(year, month - 1, 1);
    }
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
};

const formatFutureTestDate = (
  scheduledDate?: string | null,
  scheduledMonthYear?: string | null
): string => {
  if (scheduledMonthYear) {
    const monthDate = parseMonthYearValue(scheduledMonthYear);
    if (monthDate) {
      const month = String(monthDate.getMonth() + 1).padStart(2, '0');
      const year = monthDate.getFullYear();
      return `${month}/${year}`;
    }
    return scheduledMonthYear;
  }

  if (!scheduledDate) return '-';

  const date = new Date(scheduledDate);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString('en-GB');
};

const normalizeFutureTestItem = (item: FutureTestItem): FutureTestItem => {
  const modality = resolveFutureTestModality(item);
  const scheduledMonthYear = resolveScheduledMonthYear(item);

  return {
    ...item,
    modality: modality ?? undefined,
    scheduled_month_year: scheduledMonthYear,
    scheduled_date: scheduledMonthYear ? null : item.scheduled_date,
  };
};
const Appointments = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);
  const [tableActiveTab, setTableActiveTab] =
    useState<AppointmentsTableActiveTab>('all');
  const [isInuviBookingModalOpen, setIsInuviBookingModalOpen] = useState<boolean>(false);
  const [inuviBookingData, setInuviBookingData] = useState<any>(null);
  const [isFutureTestsModalOpen, setIsFutureTestsModalOpen] =
    useState<boolean>(false);
  const [isPmpAlertModalOpen, setIsPmpAlertModalOpen] = useState<boolean>(false);
  const [futureTests, setFutureTests] = useState<FutureTestItem[]>([]);
  const [futureTestsLoading, setFutureTestsLoading] = useState<boolean>(false);
  const [futureTestsLoadingMore, setFutureTestsLoadingMore] = useState<boolean>(false);
  const [futureTestsHasMore, setFutureTestsHasMore] = useState<boolean>(true);
  const [futureTestsOffset, setFutureTestsOffset] = useState<number>(0);
  const [pmpAlerts, setPmpAlerts] = useState<PmpAlertItem[]>([]);
  const [pmpAlertsLoading, setPmpAlertsLoading] = useState<boolean>(false);
  const [pmpAlertsLoadingMore, setPmpAlertsLoadingMore] = useState<boolean>(false);
  const [pmpAlertsHasMore, setPmpAlertsHasMore] = useState<boolean>(true);
  const [pmpAlertsOffset, setPmpAlertsOffset] = useState<number>(0);
  const futureTestsScrollRef = useRef<HTMLDivElement | null>(null);
  const pmpAlertsScrollRef = useRef<HTMLDivElement | null>(null);
  const futureTestsScrollTimeoutRef = useRef<number | null>(null);
  const pmpAlertsScrollTimeoutRef = useRef<number | null>(null);
  const isFetchingFutureTestsRef = useRef(false);
  const isFetchingPmpAlertsRef = useRef(false);
  const [pmpAlertToEdit, setPmpAlertToEdit] = useState<PmpAlertItem | null>(null);
  const [pmpAlertToDeleteId, setPmpAlertToDeleteId] = useState<string | number | null>(null);
  const [isPmpAlertDeleteModalOpen, setIsPmpAlertDeleteModalOpen] = useState<boolean>(false);
  const [isPmpAlertDeleteLoading, setIsPmpAlertDeleteLoading] = useState<boolean>(false);
  const [futureTestToEdit, setFutureTestToEdit] = useState<FutureTestItem | null>(null);
  const [futureTestToDeleteId, setFutureTestToDeleteId] = useState<string | number | null>(null);
  const [isFutureTestDeleteModalOpen, setIsFutureTestDeleteModalOpen] = useState<boolean>(false);
  const [isFutureTestDeleteLoading, setIsFutureTestDeleteLoading] = useState<boolean>(false);
  const [isAppointmentView, setIsAppointmentView] = useState<boolean>(false);
  const futureTestsColumns = [
    { title: 'Date', key: 'date', minWidth: '140px', flex: 0 },
    { title: 'Modality', key: 'modality', minWidth: '120px', flex: 0 },
    { title: 'Panel / Body Parts', key: 'panel', minWidth: '160px', flex: 0 },
    { title: 'Notes', key: 'notes', minWidth: '200px', flex: 1 },
    { title: '', key: 'actions', minWidth: '60px', flex: 0 }
  ];
  const pmpAlertsColumns = [
    { title: 'Date due', key: 'date', minWidth: '120px', flex: 0 },
    { title: 'PMP Ref', key: 'pmp_ref', minWidth: '140px', flex: 0 },
    { title: 'Finding Title', key: 'finding_title', minWidth: '160px', flex: 0 },
    { title: 'Modality', key: 'modality', minWidth: '120px', flex: 0 },
    { title: 'Full Price', key: 'full_price', minWidth: '90px', flex: 0 },
    { title: 'Member Price', key: 'member_price', minWidth: '110px', flex: 0 },
    { title: 'Payment', key: 'pmp_payment_status', minWidth: '90px', flex: 0 },
    { title: 'Status', key: 'pmp_status', minWidth: '120px', flex: 0 },
    { title: '', key: 'actions', minWidth: '48px', flex: 0 },
  ];
  const {
    isAddAppointmentModalOpened,
    isDeleteAppointmentModalOpened,
    isViewAppointmentResultsModalOpened,
    selectedAppointmentIdToView
  } = useSelector((state: RootState) => state.member);
  const { memberData, limit } = useSelector(
    (state: RootState) => state.member
  );
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin } = useSelector((state: RootState) => state.admin);
  const role = admin.role;
  const isAdminOrSuper =
    role === UserRoleEnum.Admin || admin.is_super_admin;
  const visible =
    role !== UserRoleEnum.Doctor ||
    (assigned_doctor && role === UserRoleEnum.Doctor) ||
    isAdminOrSuper;
  const memberUserId = memberData?.user_id;
  const storageKey = useMemo(
    () => (memberUserId ? `future-tests-${memberUserId}` : ''),
    [memberUserId]
  );
  const onAddAppointmentBtnClick = () => {
    dispatch(changeModalVisibility(true));
  };

  const onCloseModal = () => {
    setIsAppointmentView(false);
    dispatch(changeModalVisibility(false));
  };

  const fetchTuliBookingInfo = useCallback(
    async (
      appointments: AppScenariosAppointmentBasePayloadAppointmentInResponse[]
    ): Promise<ExtendedAppointmentInResponse[]> => {
      const appointmentsWithBooking = appointments.filter(
        (app) => app.booking_record
      );
      const extendedAppointments =
        appointments as ExtendedAppointmentInResponse[];

      for (const appointment of appointmentsWithBooking) {
        try {
          if (appointment.booking_record?.booking_id) {
            const response: GetTuliBookingInfoResponse = await getTuliBookingInfo(
              appointment.booking_record.booking_id
            );
            if (response.status === 'success' && response.data.length > 0) {
              if (response.data[0].pharma) {
                (appointment as ExtendedAppointmentInResponse).pharmacy =
                  response.data[0].pharma;
              }
            }
          }
        } catch (error) {
          console.error('Error fetching Tuli booking info:', error);
        }
      }
      return extendedAppointments;
    },
    []
  );

  const fetchFutureTests = useCallback(
    async (currentOffset: number, isInitial = false) => {
      if (!memberData?.user_id || isFetchingFutureTestsRef.current) return;

      isFetchingFutureTestsRef.current = true;
      if (isInitial) {
        setFutureTestsLoading(true);
      } else {
        setFutureTestsLoadingMore(true);
      }

      try {
        const res = await getFutureTests({
          member_id: memberData.user_id,
          limit,
          offset: currentOffset,
        });
        const items =
          (res as any)?.results ||
          (res as any)?.future_tests ||
          (Array.isArray(res) ? res : []);
        const normalized = (items as FutureTestItem[]).map(normalizeFutureTestItem);
        const totalCount =
          typeof (res as any)?.total === 'number'
            ? (res as any).total
            : typeof (res as any)?.total_count === 'number'
              ? (res as any).total_count
              : null;

        setFutureTests((prev) => {
          const updated = isInitial ? normalized : [...prev, ...normalized];
          if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(updated));
          }
          return updated;
        });

        if (totalCount != null) {
          setFutureTestsHasMore(currentOffset + limit < totalCount);
        } else {
          setFutureTestsHasMore(normalized.length >= limit);
        }
        setFutureTestsOffset(currentOffset + limit);
      } catch (error) {
        console.error('Failed to load future tests', error);
        toast.error('Unable to load future tests');
      } finally {
        isFetchingFutureTestsRef.current = false;
        setFutureTestsLoading(false);
        setFutureTestsLoadingMore(false);
      }
    },
    [memberData?.user_id, storageKey, limit]
  );

  const fetchPmpAlerts = useCallback(
    async (currentOffset: number, isInitial = false) => {
      if (!memberData?.user_id || isFetchingPmpAlertsRef.current) return;

      isFetchingPmpAlertsRef.current = true;
      if (isInitial) {
        setPmpAlertsLoading(true);
      } else {
        setPmpAlertsLoadingMore(true);
      }

      try {
        const res = await getPmpAlerts({
          member_id: memberData.user_id,
          limit,
          offset: currentOffset,
        });
        const items = Array.isArray(res?.pmp_alerts) ? res.pmp_alerts : [];
        const totalCount =
          typeof res?.total_count === 'number' ? res.total_count : null;

        setPmpAlerts((prev) => (isInitial ? items : [...prev, ...items]));

        if (totalCount != null) {
          setPmpAlertsHasMore(currentOffset + limit < totalCount);
        } else {
          setPmpAlertsHasMore(items.length >= limit);
        }
        setPmpAlertsOffset(currentOffset + limit);
      } catch (error) {
        console.error('Failed to load PMP alerts', error);
        toast.error('Unable to load PMP alerts');
      } finally {
        isFetchingPmpAlertsRef.current = false;
        setPmpAlertsLoading(false);
        setPmpAlertsLoadingMore(false);
      }
    },
    [memberData?.user_id, limit]
  );

  const handleEmailTrackingClick = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const startDate = thirtyDaysAgo.toISOString().split('T')[0].replace(/-/g, '/');
    const endDate = today.toISOString().split('T')[0].replace(/-/g, '/');

    window.open(`https://app.sendgrid.com/email_activity?filters=%5B%7B%22val%22%3A%5B%22${memberData?.email}%22%5D%2C%22selectedFieldName%22%3A%22to_email%22%2C%22comparisonType%22%3A%22Contains%22%7D%2C%7B%22val%22%3A%5B%22${startDate}%20-%20${endDate}%22%5D%2C%22selectedFieldName%22%3A%22last_event_time%22%2C%22comparisonType%22%3A%22Between%22%7D%5D&isAndOperator=true&page=1`)
  };

  const handleInuviBookingClick = async (appointmentId: number) => {
    try {
      const response = await getInuviBookingInfo(appointmentId);

      // Check if response is null or undefined
      if (!response) {
        toast.info('Booking information is not present');
        return;
      }

      // Handle potential response wrapper
      const bookingData = response.data || response;

      if (!bookingData) {
        toast.info('Booking information is not present');
        return;
      }

      setInuviBookingData(bookingData);
      setIsInuviBookingModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch Inuvi booking information');
      console.error('Error fetching Inuvi booking info:', error);
    }
  };

  const closeInuviBookingModal = () => {
    setIsInuviBookingModalOpen(false);
    setInuviBookingData(null);
  };

  useEffect(() => {
    const fetchAppointments = async (
      currentSelectedFilter: AppointmentsTableActiveTab
    ) => {
      if (
        currentSelectedFilter === 'future-tests' ||
        currentSelectedFilter === 'pmp-alerts'
      ) {
        return;
      }
      try {
        setIsLoading(true);
        const appointments = await getAppointmentsList(
          limit,
          0,
          memberUserId!,
          AppointmentLoadStrategy.ALL_DATA,
          currentSelectedFilter === 'upcoming'
            ? AppointmentFilter.Upcoming
            : currentSelectedFilter === 'past'
              ? AppointmentFilter.Past
              : undefined
        );

        // Fetch Tuli booking info for appointments with booking records
        const appointmentsWithPharmacy = await fetchTuliBookingInfo(
          appointments.appointments
        );
        dispatch(setAppointmentsList(appointmentsWithPharmacy));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (memberUserId) fetchAppointments(tableActiveTab);
  }, [tableActiveTab, tableKey, memberUserId, limit, dispatch, fetchTuliBookingInfo]);

  useEffect(() => {
    if (tableActiveTab === 'future-tests') {
      setFutureTestsOffset(0);
      setFutureTestsHasMore(true);
      isFetchingFutureTestsRef.current = false;
      fetchFutureTests(0, true);
    }
  }, [tableActiveTab, fetchFutureTests, memberUserId]);

  useEffect(() => {
    if (tableActiveTab === 'pmp-alerts') {
      setPmpAlertsOffset(0);
      setPmpAlertsHasMore(true);
      isFetchingPmpAlertsRef.current = false;
      fetchPmpAlerts(0, true);
    }
  }, [tableActiveTab, fetchPmpAlerts, memberUserId]);

  const handleFutureTestsScrollCheck = useCallback(() => {
    if (
      !futureTestsScrollRef.current ||
      !futureTestsHasMore ||
      isFetchingFutureTestsRef.current
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = futureTestsScrollRef.current;
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

    if (isNearBottom || isContentSmallerThanContainer) {
      fetchFutureTests(futureTestsOffset);
    }
  }, [futureTestsHasMore, futureTestsOffset, fetchFutureTests]);

  const handlePmpAlertsScrollCheck = useCallback(() => {
    if (
      !pmpAlertsScrollRef.current ||
      !pmpAlertsHasMore ||
      isFetchingPmpAlertsRef.current
    ) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = pmpAlertsScrollRef.current;
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

    if (isNearBottom || isContentSmallerThanContainer) {
      fetchPmpAlerts(pmpAlertsOffset);
    }
  }, [pmpAlertsHasMore, pmpAlertsOffset, fetchPmpAlerts]);

  useEffect(() => {
    if (tableActiveTab !== 'future-tests') return;
    const container = futureTestsScrollRef.current;
    if (!container) return;

    const onScroll = () => {
      if (futureTestsScrollTimeoutRef.current) {
        window.clearTimeout(futureTestsScrollTimeoutRef.current);
      }
      futureTestsScrollTimeoutRef.current = window.setTimeout(() => {
        handleFutureTestsScrollCheck();
      }, 100);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    if (futureTestsHasMore && futureTests.length > 0 && !futureTestsLoading) {
      setTimeout(() => handleFutureTestsScrollCheck(), 100);
    }

    return () => {
      container.removeEventListener('scroll', onScroll);
      if (futureTestsScrollTimeoutRef.current) {
        window.clearTimeout(futureTestsScrollTimeoutRef.current);
      }
    };
  }, [
    tableActiveTab,
    handleFutureTestsScrollCheck,
    futureTestsHasMore,
    futureTests.length,
    futureTestsLoading,
  ]);

  useEffect(() => {
    if (tableActiveTab !== 'pmp-alerts') return;
    const container = pmpAlertsScrollRef.current;
    if (!container) return;

    const onScroll = () => {
      if (pmpAlertsScrollTimeoutRef.current) {
        window.clearTimeout(pmpAlertsScrollTimeoutRef.current);
      }
      pmpAlertsScrollTimeoutRef.current = window.setTimeout(() => {
        handlePmpAlertsScrollCheck();
      }, 100);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    if (pmpAlertsHasMore && pmpAlerts.length > 0 && !pmpAlertsLoading) {
      setTimeout(() => handlePmpAlertsScrollCheck(), 100);
    }

    return () => {
      container.removeEventListener('scroll', onScroll);
      if (pmpAlertsScrollTimeoutRef.current) {
        window.clearTimeout(pmpAlertsScrollTimeoutRef.current);
      }
    };
  }, [
    tableActiveTab,
    handlePmpAlertsScrollCheck,
    pmpAlertsHasMore,
    pmpAlerts.length,
    pmpAlertsLoading,
  ]);

  const handlePmpAlertStatusSaved = (alertId: string | number, next: PmpAlertStatus) => {
    setPmpAlerts((prev) =>
      prev.map((alert) =>
        String(alert.id) === String(alertId) ? { ...alert, pmp_status: next } : alert
      )
    );
  };

  const handlePmpAlertSubmit = (saved: PmpAlertItem) => {
    setPmpAlerts((prev) => {
      const existingIdx = prev.findIndex((alert) => String(alert.id) === String(saved.id));
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = saved;
        return updated;
      }
      return [saved, ...prev];
    });
    setPmpAlertToEdit(null);
    setTableActiveTab('pmp-alerts');
  };

  const handlePmpAlertAction = (id: string | number, action: string) => {
    const target = pmpAlerts.find((alert) => String(alert.id) === String(id));
    if (!target) return;

    if (action === 'edit') {
      setPmpAlertToEdit(target);
      setIsPmpAlertModalOpen(true);
      return;
    }
    if (action === 'delete') {
      setPmpAlertToDeleteId(id);
      setIsPmpAlertDeleteModalOpen(true);
    }
  };

  const confirmDeletePmpAlert = async () => {
    if (!pmpAlertToDeleteId) return;
    setIsPmpAlertDeleteLoading(true);
    try {
      await deletePmpAlert(pmpAlertToDeleteId);
      setPmpAlerts((prev) =>
        prev.filter((alert) => String(alert.id) !== String(pmpAlertToDeleteId))
      );
      toast.success('PMP alert deleted');
    } catch (error) {
      console.error('Failed to delete PMP alert', error);
      toast.error('Unable to delete PMP alert');
    } finally {
      setIsPmpAlertDeleteLoading(false);
      setIsPmpAlertDeleteModalOpen(false);
      setPmpAlertToDeleteId(null);
    }
  };

  const closeDeletePmpAlertModal = () => {
    setIsPmpAlertDeleteModalOpen(false);
    setPmpAlertToDeleteId(null);
  };

  const handleFutureTestSubmit = (created: FutureTestItem) => {
    const normalized = normalizeFutureTestItem(created);
    setFutureTests((prev) => {
      const existingIdx = prev.findIndex((p) => String(p.id) === String(normalized.id));
      let updated;
      if (existingIdx >= 0) {
        updated = [...prev];
        updated[existingIdx] = normalized;
      } else {
        updated = [normalized, ...prev];
      }
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      }
      return updated;
    });
    setFutureTestToEdit(null);
    setTableActiveTab('future-tests');
  };

  const handleFutureTestAction = async (id: string | number, action: string) => {
    const target = futureTests.find((ft) => String(ft.id) === String(id));
    if (!target) return;

    if (action === 'edit') {
      try {
        const test = (await getFutureTestById(id)) as FutureTestItem;
        const normalized = {
          ...test,
          panel_id: test.panel_id ?? test.panel?.id,
          panel_ids: test.panel_ids?.length
            ? test.panel_ids
            : test.panel_id
              ? [test.panel_id]
              : undefined,
        };
        setFutureTestToEdit(normalized);
      } catch (error) {
        console.error('Failed to load future test', error);
        toast.error('Unable to load future test');
        return;
      }
      setIsFutureTestsModalOpen(true);
      return;
    }
    if (action === 'delete') {
      setFutureTestToDeleteId(id);
      setIsFutureTestDeleteModalOpen(true);
    }
  };

  const confirmDeleteFutureTest = async () => {
    if (!futureTestToDeleteId) return;
    setIsFutureTestDeleteLoading(true);
    try {
      await deleteFutureTest(futureTestToDeleteId);
      setFutureTests((prev) => {
        const updated = prev.filter((ft) => String(ft.id) !== String(futureTestToDeleteId));
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(updated));
        }
        return updated;
      });
      toast.success('Future test deleted');
    } catch (error) {
      console.error('Failed to delete future test', error);
      toast.error('Unable to delete future test');
    } finally {
      setIsFutureTestDeleteLoading(false);
      setIsFutureTestDeleteModalOpen(false);
      setFutureTestToDeleteId(null);
    }
  };

  const closeDeleteFutureTestModal = () => {
    setIsFutureTestDeleteModalOpen(false);
    setFutureTestToDeleteId(null);
  };

  return (
    <div className='appointments-container'>
      <div className='appointments-header-row sideBarPad2'>
        <div className='table-tab-navigation'>
          <button
            className={tableActiveTab === 'all' ? 'active' : ''}
            onClick={() => setTableActiveTab('all')}
          >
            All
          </button>
          <button
            className={tableActiveTab === 'upcoming' ? 'active' : ''}
            onClick={() => setTableActiveTab('upcoming')}
          >
            Upcoming
          </button>
          <button
            className={tableActiveTab === 'past' ? 'active' : ''}
            onClick={() => setTableActiveTab('past')}
          >
            Past
          </button>
          {isAdminOrSuper && (
            <button
              className={tableActiveTab === 'future-tests' ? 'active' : ''}
              onClick={() => setTableActiveTab('future-tests')}
            >
              Future Tests
            </button>
          )}
          {isAdminOrSuper && (
            <button
              className={tableActiveTab === 'pmp-alerts' ? 'active' : ''}
              onClick={() => setTableActiveTab('pmp-alerts')}
            >
              PMP Alerts
            </button>
          )}
        </div>
        {visible &&
          <div className='d-flex gap-2'>
            <div style={{ marginRight: "15px" }}>
              <Button
                text='Email Tracking'
                onClick={handleEmailTrackingClick}
                width='150px' /></div>
            {isAdminOrSuper && (
              <div style={{ marginRight: '12px' }}>
                <Button
                  text='Add Future Test'
                  onClick={() => setIsFutureTestsModalOpen(true)}
                  width='150px'
                />
              </div>
            )}
            {isAdminOrSuper && (
              <div style={{ marginRight: '12px' }}>
                <Button
                  text='Add PMP Alert'
                  onClick={() => {
                    setPmpAlertToEdit(null);
                    setIsPmpAlertModalOpen(true);
                    setTableActiveTab('pmp-alerts');
                  }}
                  width='150px'
                />
              </div>
            )}
            <Button
              isSecondary={true}
              text='Add Appointment'
              onClick={onAddAppointmentBtnClick}
            />
          </div>}
      </div>
      {tableActiveTab === 'future-tests' ? (
        <div className='future-tests-table members-appointments-table'>
          <div className='table-container'>
            <TableHeader headerColumns={futureTestsColumns} />
            <div className='member-table-body' ref={futureTestsScrollRef}>
              {futureTestsLoading ? (
                <div className='no-data'>Loading...</div>
              ) : futureTests.length === 0 ? (
                <div className='no-data'>No future tests added yet.</div>
              ) : (
                <>
                  {futureTests.map((ft, idx) => (
                    <div key={`${ft.id || idx}-${idx}`} className='table-row future-test-row'>
                      <div className='date'>
                        {formatFutureTestDate(
                          ft.scheduled_date,
                          resolveScheduledMonthYear(ft)
                        )}
                      </div>
                      <div className='modality'>
                        {getFutureTestModalityLabel(resolveFutureTestModality(ft))}
                      </div>
                      <div className='appointment-name'>
                        {getFutureTestDetailsLabel(ft)}
                      </div>
                      <div className='notes'>{ft.notes || '-'}</div>
                      <div className='actions'>
                        <TableRowActionMenu
                          options={[
                            { key: 'edit', value: 'Edit' },
                            { key: 'delete', value: 'Delete' }
                          ]}
                          id={ft.id || idx}
                          onActionMenuClick={handleFutureTestAction}
                        />
                      </div>
                    </div>
                  ))}
                  {futureTestsLoadingMore && (
                    <div className='no-data' style={{ padding: '12px' }}>
                      Loading more future tests...
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : tableActiveTab === 'pmp-alerts' ? (
        <div className='pmp-alerts-table members-appointments-table'>
          <div className='table-container'>
            <TableHeader headerColumns={pmpAlertsColumns} />
            <div className='member-table-body' ref={pmpAlertsScrollRef}>
              {pmpAlertsLoading ? (
                <div className='no-data'>Loading...</div>
              ) : pmpAlerts.length === 0 ? (
                <div className='no-data'>No PMP alerts added yet.</div>
              ) : (
                <>
                  {pmpAlerts.map((alert, idx) => (
                    <div
                      key={`${alert.id || idx}-${idx}`}
                      className='table-row pmp-alert-row'
                    >
                      <div className='date'>
                        {formatFutureTestDate(
                          alert.scheduled_date,
                          alert.scheduled_month_year
                        )}
                      </div>
                      <div className='pmp-ref'>
                        {alert.pmp_reference_number || '-'}
                      </div>
                      <div className='finding-title'>
                        {alert.finding_title || '-'}
                      </div>
                      <div className='modality'>
                        {alert.modality?.name || '-'}
                      </div>
                      <div className='pmp-alert-full-price'>
                        {alert.full_price != null
                          ? Number(alert.full_price).toFixed(2)
                          : '-'}
                      </div>
                      <div className='pmp-alert-member-price'>
                        {alert.member_price != null
                          ? Number(alert.member_price).toFixed(2)
                          : '-'}
                      </div>
                      <div className='pmp-alert-payment'>
                        {alert.pmp_payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                      </div>
                      <div className='pmp-alert-status'>
                        <PmpAlertInlineStatusSelect
                          alert={alert}
                          disabled={!isAdminOrSuper}
                          onStatusSaved={(next) => handlePmpAlertStatusSaved(alert.id, next)}
                        />
                      </div>
                      <div className='actions'>
                        <TableRowActionMenu
                          options={[
                            { key: 'edit', value: 'Edit' },
                            { key: 'delete', value: 'Delete' },
                          ]}
                          id={alert.id || idx}
                          onActionMenuClick={handlePmpAlertAction}
                        />
                      </div>
                    </div>
                  ))}
                  {pmpAlertsLoadingMore && (
                    <div className='no-data' style={{ padding: '12px' }}>
                      Loading more PMP alerts...
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <AppointmentsList
          currentSelectedFilter={tableActiveTab}
          key={`${tableActiveTab}-${tableKey}`}
          isLoading={isLoading}
          onInuviBookingClick={handleInuviBookingClick}
          setIsAppointmentView={setIsAppointmentView}
        />
      )}
      <FutureTestsModal
        isOpen={isFutureTestsModalOpen}
        onClose={() => setIsFutureTestsModalOpen(false)}
        onSubmit={handleFutureTestSubmit}
        memberId={memberData?.user_id}
        initialTest={futureTestToEdit}
      />
      <CreatePmpAlertModal
        isOpen={isPmpAlertModalOpen}
        onClose={() => {
          setIsPmpAlertModalOpen(false);
          setPmpAlertToEdit(null);
        }}
        onSubmit={handlePmpAlertSubmit}
        memberId={memberData?.user_id}
        initialAlert={pmpAlertToEdit}
      />
      <ConfirmModal
        isOpen={isPmpAlertDeleteModalOpen}
        onClose={closeDeletePmpAlertModal}
        onConfirmClick={confirmDeletePmpAlert}
        title='Delete PMP alert?'
        subtitle='This action cannot be undone.'
        confirmBtnText='Delete'
        cancelBtnText='Cancel'
        loading={isPmpAlertDeleteLoading}
      />
      <ConfirmModal
        isOpen={isFutureTestDeleteModalOpen}
        onClose={closeDeleteFutureTestModal}
        onConfirmClick={confirmDeleteFutureTest}
        title='Delete future test?'
        subtitle='This action cannot be undone.'
        confirmBtnText='Delete'
        cancelBtnText='Cancel'
        loading={isFutureTestDeleteLoading}
      />
      <AppointmentModal
        isOpen={isAddAppointmentModalOpened}
        onClose={onCloseModal}
        onAddAppointmentCreate={() => setTableKey((prev) => prev + 1)}
        isAppointmentView={isAppointmentView}
      />
      <DeleteAppointmentModal
        isOpen={isDeleteAppointmentModalOpened}
        onClose={() => dispatch(changeDeleteModalVisibility(false))}
      />
      {isViewAppointmentResultsModalOpened && (
        <ShowBiomarkersTestResultsModal
          isOpen={isViewAppointmentResultsModalOpened}
          onClose={() =>
            dispatch(changeViewAppointmentResultsModalVisibility(false))
          }
          resultsType={BiomarkerResultType.PUBLISHED}
          appointmentId={selectedAppointmentIdToView!}
          member_id={memberData?.user_id!}
        />
      )}
      <InuviBookingModal
        isOpen={isInuviBookingModalOpen}
        onClose={closeInuviBookingModal}
        bookingData={inuviBookingData}
      />
    </div>
  );
};

export default Appointments;
